"use client"

import React, {useEffect, useState} from 'react';
import Header2 from "@/app/(index)/header2";
import cx from "clsx";
import {Noto_Sans_TC, Rowdies} from 'next/font/google';
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import Footer from "@/app/(index)/footer";
import Bread from "@/app/(index)/bread";
import styles from '../index.module.css'
import {Divider, Pagination, Select, Text, TextInput} from "@mantine/core";
import {IconSearch} from "@tabler/icons-react";
import {DatesProvider} from "@mantine/dates";
import '@mantine/dates/styles.css';
import "dayjs/locale/zh-tw";
import {useForm} from "@mantine/form";
import {indexEbookQuery, loadEbookYears} from "@/service";
import {useDebouncedValue, useMediaQuery} from "@mantine/hooks";
import {EbookFieldOptions, IEBookQueryInputForm, IEBookQueryOutputForm, SelectOption} from "@/form/publication";
import EBookItem from "@/app/(index)/ebook/EbookItem";

const notoSansTC = Noto_Sans_TC({
    weight: ['400', '500', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

const rowdies = Rowdies({
    weight: ['300', '400', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});



const Page = ({ params }: { params: { id: string } }) => {
    const { fontSize, lang } = useGlobalStateStore();
    const [outputResult, setOutputResult] = useState<IEBookQueryOutputForm | null>(null);
    const [viewType, setViewType] = useState<number>(0);
    const [init, setInit] = useState<boolean>(true);
    const [value, setValue] = useState('');
    const [debounced] = useDebouncedValue(value, 500);
    const [years, setYears] = useState<SelectOption[]>([]);
    const [queryError, setQueryError] = useState<string | null>(null);

    const form = useForm<IEBookQueryInputForm>({
        mode: 'controlled',
        initialValues: {
            queryText: '',
            year: null,
            field: null,
            rowsPerPage: 12,
            nowPage: 1
        }
    });

    useEffect(() => {
        loadEbookYears()
            .then(data => {
                const ebookYears: SelectOption[] = data.map(year => ({
                    value: year.toString(),
                    label: `${year}年`
                }));
                setYears(ebookYears);
                if(ebookYears.length > 0){
                    form.setValues({ year: ebookYears[0].value });
                }
                else{
                    setQueryError('目前沒有報告／專書資料');
                }
                setInit(false);
            })
            .catch(() => {
                setQueryError('資料載入失敗，請稍後再試');
                setInit(false);
            });
    }, []);

    const handleChangeNowPage = (n: number) => {
        form.setValues({ nowPage: n });
        // 直接傳入新頁碼，避免 race condition
        setQueryError(null);
        indexEbookQuery({ ...form.getValues(), nowPage: n })
            .then(result => setOutputResult(result))
            .catch(() => {
                setOutputResult(null);
                setQueryError('資料載入失敗，請稍後再試');
            });
    }

    useEffect(() => {
        if(!init){
            form.setValues({
                nowPage: 1,
                rowsPerPage: form.getValues().rowsPerPage,
                queryText: debounced,
            });
            query();
        }
    }, [form.getValues().rowsPerPage, form.getValues().year, form.getValues().field, debounced]);

    const query = () => {
        if(form.getValues().year === null){
            return;
        }
        setQueryError(null);
        indexEbookQuery(form.getValues())
            .then(result => setOutputResult(result))
            .catch(() => {
                setOutputResult(null);
                setQueryError('資料載入失敗，請稍後再試');
            });
    }
    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');
    return (
        <DatesProvider settings={{ locale: 'zh-tw', firstDayOfWeek: 1, timezone: 'UTC'}}>
            <div className={cx("min-h-screen flex flex-col justify-between", notoSansTC.className)}>
                <div>
                    <Header2/>
                    {
                        mediaQueryLessSM && (
                            <Bread name='研究成果與出版品 - 報告/專書'/>
                        )
                    }
                    <div
                        className={cx("mx-auto mb-[100px]", mediaQueryLessSM ? 'w-[1250px]' : 'w-full mt-[100px] px-[10px]')}>
                        <div
                            className="bg-[url('/images/title_topBG.png')] bg-[length:100%_5px] bg-no-repeat bg-top inline-block pt-[15px] pb-[25px] text-[#5b5b5b] text-[35px] font-bold">
                            報告/專書
                        </div>

                        <form className={cx("flex justify-between", mediaQueryLessSM ? '' : 'flex-col')}>
                            <div className="flex gap-[20px] items-end">
                                <TextInput leftSection={<IconSearch/>}
                                           value={value}
                                           placeholder="請輸入查詢字串..."
                                           onChange={(event) => setValue(event.currentTarget.value)}
                                />

                                <div>
                                    <div className="flex gap-1">
                                        <Text c={"dark"} size="md">年度</Text>
                                        <Text c={"red"} size="md" fw={700}>*</Text>
                                    </div>
                                    <Select data={years}
                                            key={form.key('year')}
                                            {...form.getInputProps('year')}
                                    />
                                </div>
                                <div>

                                    <Text c={"dark"} size="md">領域</Text>
                                    <Select data={EbookFieldOptions}
                                            clearable
                                            placeholder={"請選擇領域"}
                                            key={form.key('field')}
                                            {...form.getInputProps('field')}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-[20px]">
                                <div className="flex items-center">
                                    <div className="text-[#333333]">每頁顯示</div>
                                    <select className={styles.select}
                                            key={form.key('rowsPerPage')}
                                            {...form.getInputProps('rowsPerPage')}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <div className="text-[#333333]">筆</div>
                                </div>
                            </div>
                        </form>


                        <div className="text-[30px] font-bold pt-8">
                            {form.getValues().year}年出版品
                        </div>

                        <Divider color="#cedbdc" my={30}/>

                        {/*bg-[url('/images/BG-booklist.jpg)]*/}
                        <div className={cx(" bg-[url('/images/BG-booklist.jpg')] bg-no-repeat bg-top")}>
                            {
                                queryError !== null && (
                                    <div className="text-3xl font-bold text-red-600">{queryError}</div>
                                )
                            }

                            {
                                queryError === null && outputResult?.obj?.map((item, index) => (
                                    <EBookItem item={item} key={`ebk-${index}`}/>
                                ))
                            }

                            {
                                queryError === null && outputResult?.totalRows == 0 && (
                                    <div className="text-3xl font-bold">查無資料，請重新查詢</div>
                                )
                            }

                            {
                                (outputResult?.totalPages ?? 0) > 1 && (
                                    <div className="mt-10 flex justify-end">
                                        <Pagination total={outputResult?.totalPages!!}
                                                    value={outputResult?.nowPage}
                                                    radius={"xl"}
                                                    color="#316d68"
                                                    siblings={2}
                                                    className={cx(rowdies.className, "font-light")}
                                                    onChange={handleChangeNowPage} mt="sm"/>
                                    </div>
                                )
                            }
                        </div>

                    </div>
                </div>
                <Footer/>
            </div>
        </DatesProvider>
    );
}
export default Page
